import { decodeJwtClaims } from '../lib/jwt'
import { Role } from '../types/prisma-enums'
import { getParentToken } from './parentSession'
import { getStaffSession } from './staffSession'

/**
 * The signed-in principal, recovered from storage on boot.
 *
 * 🔴 THIS IS THE FUNCTION THE PRODUCT WAS MISSING, AND ITS ABSENCE WAS THE WHOLE
 * BUG. `parentSession.ts` had written the pair to `localStorage` since C1, and
 * nothing ever read it back at start-up: `AppRouter` opened with
 * `useState<Session | null>(null)`, `mayNavigate` was therefore asked about a
 * principal with no role, and every guarded route bounced to `#/start`. A parent
 * with a perfectly good thirty-day credential on disk was shown a sign-in form
 * on every reload — and the back button, which lands on a route the guard treats
 * exactly like a reload, produced the same thing. Persisting a credential nobody
 * reads is indistinguishable from not persisting one.
 *
 * 🔴 IT IS A MODULE FUNCTION RATHER THAN A HOOK, AND THAT IS WHAT MAKES IT
 * TESTABLE. The repo has no DOM renderer (OD-60), so a boot decision made inside
 * a `useState` initialiser is a decision no test can reach. Both stores take an
 * injectable storage shim, so `restore.test.ts` drives this end to end without a
 * browser.
 */
export interface RestoredSession {
  role: Role
  token: string
  /** X4 — only ever set for a session already in memory. See `restoreSession`. */
  impersonatorId?: string | null
}

/**
 * Whoever this browser is still signed in as, or `null`.
 *
 * 🔴 STAFF FIRST, AND THE ORDER IS A CORRECTNESS REQUIREMENT RATHER THAN A
 * PREFERENCE. `refresh.ts`'s `tierToRotate()` asks `getStaffSession()` first and
 * only falls through to the parent store when there is no staff session — so if
 * this restored the parent while a staff pair sat in storage, every rotation the
 * restored session triggered would rotate the OTHER tier's credential and hand
 * back a token for the wrong person. One precedence, stated in two places that
 * cannot disagree.
 *
 * 🔴 THE ROLE COMES FROM THE TOKEN'S OWN CLAIM, NEVER FROM WHICH KEY IT WAS
 * FOUND UNDER. `AppRouter`'s `verifyOtp` states the rule for the sign-in path —
 * *"fail closed on the claim rather than assuming the route's name"* — and a
 * restore is the same decision at a different moment. A pair under
 * `kalqy.parent` whose claim does not say `PARENT` is not a parent session; it
 * is something we cannot describe, and the honest answer is a door.
 *
 * ⚠️ AN EXPIRED ACCESS TOKEN IS STILL RESTORED, AND THAT IS THE POINT. `exp` is
 * unverified here and is never an authorisation decision (both stores say so at
 * length); the refresh token beside it is what makes the session recoverable,
 * and `client.ts` rotates on the first 401. Refusing on our own clock would sign
 * out every user whose laptop has the wrong date — and, at fifteen minutes of
 * `JWT_ACCESS_TTL`, very nearly everybody who closed the tab yesterday.
 *
 * ⚠️ IT NEVER TOUCHES THE NETWORK. A boot that awaited a rotation before it
 * could decide which screen to draw would put a spinner in front of a dashboard
 * whose data is about to be fetched anyway, and would fail closed on a bad
 * connection — signing out exactly the person whose wifi is worst.
 */
export function restoreSession(): RestoredSession | null {
  /*
   * ⚠️ `getStaffSession()` IS WHAT HYDRATES THE STAFF STORE, so this call is the
   * boot read as well as the question. Calling it before the parent branch is
   * therefore also what makes `tierToRotate()` see the same tier this did.
   */
  const staff = getStaffSession()
  if (staff) {
    return {
      role: staff.role,
      token: staff.accessToken,
      /*
       * 🔴 THIS IS ONLY EVER NON-NULL FOR A SESSION THAT WAS ALREADY IN MEMORY.
       * An impersonation grant is never written to storage (`staffSession.ts`
       * §beginImpersonation), so a genuine boot cannot produce one. It is passed
       * through rather than dropped because dropping it would render the
       * impersonated tier with NO BANNER — the silent view-as `admin-panel.md`
       * §8 forbids — if this is ever called from anywhere but a cold start.
       */
      impersonatorId: staff.impersonatorId,
    }
  }

  const token = getParentToken()
  if (!token) return null

  /*
   * Unparseable, or a claim naming a role this build does not know: no session.
   * `sessionFromTokens` fails closed the same way one layer over, and for the
   * same reason — a signed-in user with a role nothing recognises gets an empty
   * menu and no way to understand why.
   */
  const claims = decodeJwtClaims(token)
  if (claims?.role !== Role.PARENT) return null

  return { role: Role.PARENT, token }
}
